import { isVisibilityRule, type Section, type Field } from '@eformulieren/model'
import { type FieldCheck, fieldCheckPasses, onSection } from './check.js'

/**
 * Waar een veld staat: in welke sectie, en of het een rijveld van een
 * herhaalgroep is.
 *
 * De evaluator, de projectie en de verschilmarkering hebben allemaal die vraag,
 * en het antwoord staat hier één keer. Het onderscheid met een rijveld is
 * wezenlijk: het antwoord op een gewoon veld staat op de stand onder zijn eigen
 * id, en het antwoord op een rijveld staat per rij in het antwoord van zijn
 * herhaalgroep (R-40, R-41, ontwerp 4.5).
 */
export interface FieldPlace {
  readonly veld: Field
  readonly sectieId: string
  /** Gevuld als dit veld een rijveld is; dan is dit het id van de herhaalgroep. */
  readonly herhaalgroepId?: string
}

/** Alle velden van deze secties, rijvelden erbij, op hun stabiele id. */
export function fieldsIndex(sections: readonly Section[]): Map<string, FieldPlace> {
  const index = new Map<string, FieldPlace>()
  for (const section of sections) {
    for (const field of section.velden) {
      index.set(field.id, { veld: field, sectieId: section.id })
      if (field.control === 'herhaalgroep') {
        for (const rowField of field.rijVelden) {
          index.set(rowField.id, { veld: rowField, sectieId: section.id, herhaalgroepId: field.id })
        }
      }
    }
  }
  return index
}

/**
 * De velden die niet leeg mogen blijven, over alle meegegeven secties.
 *
 * **Verplicht is een regelsoort en geen vlag op het veld** (R-26, ontwerp 6.2). De
 * aanduiding die R-09 aan de invuller vraagt volgt dus uit de regels, en die afleiding hoort
 * bij de evaluator zodat elke afnemer haar één keer krijgt in plaats van hem zelf te maken
 * (ontwerp 6.2 wijst hem expliciet aan `packages/engine` toe). Zonder dat zou een veld als
 * verplicht gemarkeerd kunnen staan terwijl indienen niet blokkeert, of andersom.
 *
 * **Elke harde regel die op een leeg antwoord faalt, niet alleen `verplicht`** (audit van 30
 * oktober 2026). Een akkoord dat ja moet zijn (`akkoordJa`) en een minimum aantal
 * (`minimumAantal`) falen ook als er niets staat; zo'n veld droeg "(optioneel)" en gaf bij
 * indienen toch een harde melding, en dat is precies wat R-09 moet voorkomen. Welke regels op
 * leeg falen staat niet in een lijst hier maar komt uit `fieldCheckPasses`, de functie waarmee de
 * evaluator controleert: een nieuwe regelsoort valt er zo vanzelf goed in.
 *
 * **Alleen de harde regels.** Een regel is per regel om te zetten naar een waarschuwing (D-15),
 * en een waarschuwing blokkeert indienen nooit (R-23, R-31). Zou de aanduiding ook op zo'n regel
 * staan, dan vertelt het formulier de invuller dat hij iets moet invullen terwijl hij dat niet
 * moet. Ontwerp 6.2 schrijft de aanduiding aan het bestaan van de regel toe zonder het mechanisme
 * te noemen; dit is de lezing die de invuller niets onwaars vertelt.
 */
export function requiredFields(sections: readonly Section[]): ReadonlySet<string> {
  return new Set(requiredChecks(sections).keys())
}

/**
 * Per veld de harde regels die op een leeg antwoord falen (zie {@link requiredFields}).
 *
 * Voor wie wil weten of zo'n veld is ingevuld: dat is het als deze regels voldoen, en niet als
 * het antwoord niet leeg is. Een akkoord op nee is niet leeg, maar ook niet gegeven.
 */
export function requiredChecks(sections: readonly Section[]): ReadonlyMap<string, readonly FieldCheck[]> {
  const required = new Map<string, FieldCheck[]>()
  for (const section of sections) {
    for (const rule of section.regels) {
      if (isVisibilityRule(rule) || onSection(rule)) continue
      if (rule.mechanisme !== 'hard') continue
      if (fieldCheckPasses(rule, undefined)) continue
      const list = required.get(rule.doelVeld) ?? []
      list.push(rule)
      required.set(rule.doelVeld, list)
    }
  }
  return required
}

/**
 * De teken- en woordgrenzen per veld, met het mechanisme erbij.
 *
 * R-12 vraagt een teller die meeloopt en laat zien hoeveel er is gebruikt en
 * hoeveel er nog over is; die teller heeft de grens nodig, en die staat in de
 * regels en niet op het veld (R-26). Dezelfde afleiding als `requiredFields`, en
 * om dezelfde reden hier: één keer, voor elke afnemer.
 *
 * **Het mechanisme reist mee en de grens wordt niet gefilterd.** Anders dan bij de
 * verplicht-aanduiding hoort een teller er ook te staan als de regel een
 * waarschuwing is: de teller vertelt hoe lang het antwoord is, en dat is waar
 * ongeacht of overschrijden indienen tegenhoudt. Wat het mechanisme bepaalt is of
 * de teller bij overschrijding een blokkade aankondigt (hard, R-13) of een advies
 * (waarschuwing, R-23, R-31).
 */
export interface Boundary {
  readonly grens: number
  readonly mechanisme: 'hard' | 'waarschuwing'
}

export interface FieldLimits {
  readonly maximumTekens?: Boundary
  readonly maximumWoorden?: Boundary
  readonly minimumWoorden?: Boundary
}

export function limitsOf(sections: readonly Section[]): ReadonlyMap<string, FieldLimits> {
  const per = new Map<string, FieldLimits>()
  const fill = (fieldId: string, part: FieldLimits): void => {
    per.set(fieldId, { ...per.get(fieldId), ...part })
  }

  for (const section of sections) {
    for (const rule of section.regels) {
      switch (rule.soort) {
        case 'maximumTekens':
          fill(rule.doelVeld, { maximumTekens: { grens: rule.maximum, mechanisme: rule.mechanisme } })
          break
        case 'maximumWoorden':
          fill(rule.doelVeld, { maximumWoorden: { grens: rule.maximum, mechanisme: rule.mechanisme } })
          break
        case 'minimumWoorden':
          fill(rule.doelVeld, { minimumWoorden: { grens: rule.minimum, mechanisme: rule.mechanisme } })
          break
        default:
          break
      }
    }
  }
  return per
}

/**
 * De bestandseisen per bijlageveld (R-104).
 *
 * Typen, maximumaantal en maximale grootte staan in één regelsoort, zodat twee
 * regels elkaar niet kunnen tegenspreken over hetzelfde bijlageveld (zie de
 * motivering bij `MULTI_VALUED` in het model). De control heeft ze nodig om het
 * juiste bestandsfilter aan te bieden en om te laten zien wat er mag; de toetsing
 * zelf doet `fieldCheckPasses`, en die blijft de enige poort.
 *
 * Anders dan bij een grens is er hier geen mechanisme-afweging: er is precies één
 * `bestandseisen`-regel per veld die zinvol is, en wat de control ervan toont is
 * beschrijvend en geen uitspraak over blokkeren.
 */
export interface FileRequirements {
  readonly typen: readonly string[]
  readonly maximumAantal: number
  readonly maximumGrootteMb: number
}

export function fileRequirementsOf(sections: readonly Section[]): ReadonlyMap<string, FileRequirements> {
  const per = new Map<string, FileRequirements>()
  for (const section of sections) {
    for (const rule of section.regels) {
      if (rule.soort !== 'bestandseisen') continue
      per.set(rule.doelVeld, {
        typen: rule.typen,
        maximumAantal: rule.maximumAantal,
        maximumGrootteMb: rule.maximumGrootteMb,
      })
    }
  }
  return per
}

/**
 * Het minimum en maximum aantal per meerwaardig veld (R-42).
 *
 * Dezelfde regelsoort begrenst het aantal rijen van een herhaalgroep, het aantal
 * trefwoorden van een `tag` en het aantal gekozen opties van een meerkeuzeveld
 * (`MULTI_VALUED` in het model). De control heeft de getallen nodig om te weten of
 * hij nog een rij mag aanbieden of de laatste mag laten weghalen; de toets zelf
 * blijft de evaluator.
 */
export interface Counts {
  readonly minimum?: number
  readonly maximum?: number
}

export function countsOf(sections: readonly Section[]): ReadonlyMap<string, Counts> {
  const per = new Map<string, Counts>()
  const fill = (fieldId: string, part: Counts): void => {
    per.set(fieldId, { ...per.get(fieldId), ...part })
  }
  for (const section of sections) {
    for (const rule of section.regels) {
      if (rule.soort === 'minimumAantal') fill(rule.doelVeld, { minimum: rule.minimum })
      else if (rule.soort === 'maximumAantal') fill(rule.doelVeld, { maximum: rule.maximum })
    }
  }
  return per
}
